import type { Coordinate } from "../../entities/tracking";
import { env } from "../constants/env";

// Tracé d'itinéraire routier via **Google Routes API** (computeRoutes).
//
// 🔐 Sécurité — le web AMÉLIORE la situation par rapport au natif.
// Sur natif, la clé était exposée au JS et les "Application restrictions"
// (bundle ID) de Google ne s'appliquaient PAS à un fetch() brut : il ne restait
// que les API restrictions et les quotas.
// Dans un navigateur, la requête porte un en-tête `Referer` que Google vérifie :
// les **HTTP referrer restrictions** deviennent efficaces. À CONFIGURER dans la
// console Google Cloud sur les domaines de déploiement, sans quoi la clé est
// utilisable par n'importe qui. C'est l'action à mener au Lot 7.
// Un proxy backend reste préférable pour de la prod à volume (TODO backend).
//
// 💰 Coût : facturé PAR CALCUL d'itinéraire (≈5 $/1000, 10 000 gratuits/mois),
// PAS par position GPS. On calcule la route une fois pour un trajet donné (cache
// TanStack côté hook) — jamais à chaque tick GPS.

const ROUTES_URL = "https://routes.googleapis.com/directions/v2:computeRoutes";
const API_KEY = env.googleMapsApiKey;

// Essentials tier : max 10 waypoints intermédiaires (au-delà → tarif Pro).
const MAX_INTERMEDIATES = 10;

function toWaypoint(c: Coordinate) {
  return { location: { latLng: { latitude: c.latitude, longitude: c.longitude } } };
}

// Décode une polyline encodée Google (algorithme standard) en coordonnées.
export function decodePolyline(encoded: string): Coordinate[] {
  const points: Coordinate[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte: number;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : result >> 1;

    result = 0;
    shift = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : result >> 1;

    points.push({ latitude: lat / 1e5, longitude: lng / 1e5 });
  }
  return points;
}

// Calcule l'itinéraire routier (DRIVE) passant par tous les `waypoints` dans
// l'ordre. Renvoie la polyline décodée, ou null si échec (l'appelant retombe
// alors sur la ligne droite). Le 1er waypoint = origine, le dernier =
// destination, les autres = arrêts intermédiaires.
export async function fetchRoutePolyline(
  waypoints: Coordinate[],
  signal?:   AbortSignal,
): Promise<Coordinate[] | null> {
  if (waypoints.length < 2 || !API_KEY) return null;

  const origin        = waypoints[0];
  const destination   = waypoints[waypoints.length - 1];
  const intermediates = waypoints.slice(1, -1).slice(0, MAX_INTERMEDIATES);

  try {
    const res = await fetch(ROUTES_URL, {
      method:  "POST",
      headers: {
        "Content-Type":     "application/json",
        "X-Goog-Api-Key":   API_KEY,
        // FieldMask minimal → reste sur le tier Essentials (pas de trafic = pas de Pro).
        "X-Goog-FieldMask": "routes.polyline.encodedPolyline",
      },
      body: JSON.stringify({
        origin:          toWaypoint(origin),
        destination:     toWaypoint(destination),
        intermediates:   intermediates.map(toWaypoint),
        travelMode:      "DRIVE",
        // ⚠️ HIGH_QUALITY, et non OVERVIEW.
        //
        // OVERVIEW rend une géométrie SIMPLIFIÉE, prévue pour une vue
        // d'ensemble : elle lisse les courbes et raccourcit les carrefours.
        // Aux niveaux de zoom du suivi (14 à 17, où l'on distingue les rues),
        // cette simplification se voit — le trait passe à côté du bitume, et
        // la Main, recalée dessus, avec lui.
        //
        // Même tarif Essentials : la qualité de polyline n'entre pas dans la
        // facturation, seul le nombre de calculs compte.
        polylineQuality: "HIGH_QUALITY",
      }),
      signal,
    });
    if (!res.ok) {
      if (env.isDev) console.warn("[routing] Routes API HTTP", res.status, await res.text().catch(() => ""));
      return null;
    }
    const json = (await res.json()) as {
      routes?: { polyline?: { encodedPolyline?: string } }[];
    };
    const encoded = json.routes?.[0]?.polyline?.encodedPolyline;
    if (!encoded) return null;
    return decodePolyline(encoded);
  } catch {
    return null; // réseau / abort → repli ligne droite côté appelant
  }
}
